/* eslint-disable react/prop-types */
import { IconButton, Menu, MenuItem } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import React, { useState } from 'react';
import { Handle, Position } from 'react-flow-renderer';
import NodeContainer, {
	MenuWrapper,
	NodeContentWrapper,
	NodeLabel,
	Text,
	NoMenuText,
	useStyles,
} from './Node.styles';
import {
	Add as AddIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
} from '@mui/icons-material';
import { getSubscriptions, rem } from '@innovyze/stylovyze';
import { fullAccess } from '@innovyze/shared-utils';
import { useGlobalization } from '@Translations';

interface InnoInputNodeProps {
	data: {
		id: string;
		name: string;
		title: string;
		text: string;
		onEditClick: (id: string) => void;
		onDeleteClick: (id: string) => void;
		onDuplicateClick: (id: string) => void;
		data: {
			label: string;
			// nodeData: [] rules, title, etc
		};
		isRehabTreeEditable: boolean;
	};
}

const InnoInputNode = (props: InnoInputNodeProps) => {
	const { t } = useGlobalization();
	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);

	const classes = useStyles();
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const {
		id,
		title,
		onEditClick,
		onDeleteClick,
		onDuplicateClick,
		isRehabTreeEditable,
	} = props.data;

	const enableEdit = isRehabTreeEditable && hasFullAccess;
	const wrapWidth = enableEdit ? 25 : 30;

	const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setMenuAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
	};

	const handleEditClick = () => {
		onEditClick(id);
		setMenuAnchorEl(null);
	};

	const handleDeleteClick = () => {
		onDeleteClick(id);
		setMenuAnchorEl(null);
	};

	const handleDuplicateClick = () => {
		onDuplicateClick(id);
		setMenuAnchorEl(null);
	};

	return (
		<NodeContainer style={{ width: rem(200), height: rem(48) }}>
			<NodeLabel>{t('Query')}</NodeLabel>
			{enableEdit ? (
				<MenuWrapper>
					<IconButton
						data-cy="threeDotMenu"
						aria-label="more"
						aria-controls="long-menu"
						aria-haspopup="true"
						size="small"
						onClick={handleMenuClick}>
						<MoreVert />
					</IconButton>
					<Menu
						id="create-node-menu"
						anchorEl={menuAnchorEl}
						open={Boolean(menuAnchorEl)}
						onClose={handleMenuClose}
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
						<MenuItem
							className={classes.nodeMenuItem}
							onClick={handleEditClick}
							data-cy="editOption">
							<EditIcon color="action" fontSize="small" />
							<span>{t('Edit')}</span>
						</MenuItem>
						<MenuItem
							className={classes.nodeMenuItem}
							onClick={handleDuplicateClick}
							data-cy="duplicateOption">
							<AddIcon color="action" fontSize="small" />
							<span>{t('Duplicate')}</span>
						</MenuItem>
						<MenuItem
							className={classes.nodeMenuItem}
							onClick={handleDeleteClick}
							data-cy="deleteOption">
							<DeleteIcon color="action" fontSize="small" />
							<span>{t('Delete')}</span>
						</MenuItem>
					</Menu>
				</MenuWrapper>
			) : (
				<> </>
			)}
			<NodeContentWrapper>
				{enableEdit ? (
					title.length >= wrapWidth ? (
						<Text title={title}>{title}</Text>
					) : (
						<Text>{title}</Text>
					)
				) : title.length >= wrapWidth ? (
					<NoMenuText title={title}>{title}</NoMenuText>
				) : (
					<NoMenuText>{title}</NoMenuText>
				)}
			</NodeContentWrapper>
			<Handle
				type="source"
				position={Position.Bottom}
				isConnectable={isRehabTreeEditable && hasFullAccess}
			/>
			<Handle
				type="target"
				position={Position.Top}
				isConnectable={isRehabTreeEditable && hasFullAccess}
			/>
		</NodeContainer>
	);
};

export default InnoInputNode;
